import React from 'react';
import {
  List, Datagrid, TextField, NumberField, FunctionField, EditButton,
  Edit, Create, TabbedForm, FormTab, TextInput, NumberInput, BooleanInput,
  DateInput, SelectInput, ReferenceArrayInput, AutocompleteArrayInput,
  ReferenceArrayField, SingleFieldList, ChipField, FormDataConsumer,
  Filter,
  required, number, minValue
} from 'react-admin';
import { Box, Card, CardContent, CardHeader } from '@material-ui/core';
import {
  TopLevelDomainFilter, DeviceTypeFilter, PageUrlFilter, CountryFilter,
  StateFilter, ViewabilityFilter, OSFilter, AsnFilter, AdSizeFilter,
  ChannelFilter, ImpressionTypeFilter, UserAgentFilter,
  MultipleChoiceFilter, browserChoices, IpListsFilter
} from '../common/filters/Filters';
import * as utils from '../components/utils';
import { CampaignStatusChip, CampaignStatusBanner } from '../components/campaignStatus';
import CampaignPerformancePanel from '../components/CampaignPerformancePanel';
import ErrorAwareFormTabs from '../components/ErrorAwareFormTabs';

const {
  validFrequencyCap, validFrequencyHours, requireSomeSpendLimit, validCampaignName
} = require('../components/validation.js');

// Two side-by-side columns, the same inline-block pair the ops campaign form
// uses (adcampaigns.js:254,316) rather than flex or grid - matched deliberately
// so the advertiser and ops forms wrap identically at the same widths.
const twoColumnLeft = { width: '45%', display: 'inline-block', verticalAlign: 'top' };
const twoColumnRight = { ...twoColumnLeft, paddingLeft: '20px' };

// Inputs nested inside a Card are not wrapped by react-admin's FormInput, so
// they inherit no width at all and collapse to their intrinsic size.
const cardInput = { width: '100%' };

const CampaignFilter = (props) => (
  <Filter {...props}>
    <TextInput label="Search" source="search_name" alwaysOn resettable />
  </Filter>
);

// Uses react-admin's stock empty state, deliberately, for visual consistency
// with every other list in the console.
//
// This replaced a custom card (NBU-7) whose copy also named a second cause of
// an empty list: an advertiser-role read is scoped by mongoPermissionsQuery
// (insights/service/index.js) to `advertiser_id: {$in: [-1]}` when the user
// document has no `advertisers` array, so a user granted the console resources
// but never linked to an advertiser sees an empty list and no error at all -
// indistinguishable, client-side, from having no campaigns, because the
// browser's permissions object carries role and resources but not
// `advertisers`. That diagnostic is no longer surfaced here. If it is wanted
// again, put it somewhere it can be shown unconditionally (the Account screen)
// rather than back in the empty state, which cannot tell the two cases apart.
export const AdvertiserCampaignList = (props) => (
  <List {...props} filters={<CampaignFilter />} perPage={25} bulkActionButtons={false}>
    <Datagrid rowClick="edit">
      <TextField source="code" label="ID" />
      <TextField source="name" label="Campaign" />
      {/* Resolved from the full gate chain, not from `active` alone - see
          components/campaignStatus.js. */}
      <FunctionField label="Status" render={record => <CampaignStatusChip record={record} />} />
      <NumberField source="bid_price" label="Bid CPM" options={{ style: 'currency', currency: 'USD' }} />
      {/* flight_start_day/flight_end_day are timezone-less YYYY-MM-DD calendar
          days (their meaning is defined by the campaign's own flight_timezone
          field). DateField parses the string as UTC midnight and re-renders
          it in the viewer's local timezone, which shows the previous day for
          any viewer west of UTC - render the raw string instead. */}
      <TextField source="flight_start_day" label="Starts" />
      <TextField source="flight_end_day" label="Ends" />
      <FunctionField label="Impressions"
        render={record => utils.long(record.current_lifetime_impressions || 0)} />
      {/* current_lifetime_revenue, NOT current_lifetime_adspend (NBU-7).
          budget_coordinator derives adspend as campaign_book_price *
          impressions / 1000, so it is $0.00 for any campaign with no book
          price and a DIFFERENT quantity from the lifetime cap, which is
          enforced against current_lifetime_revenue. Campaign 1709 read ~$3,400
          of adspend against $2,001 of the revenue that actually paused it.
          Advertisers must see the number their cap is measured in - it is also
          the number the delivery cards on the form show. */}
      <FunctionField label="Spend"
        render={record => utils.double(record.current_lifetime_revenue || 0)} />
      <EditButton variant="contained" color="primary" />
    </Datagrid>
  </List>
);

// Composed from the shared filter components, and the set is NOT a UI choice:
// it is exactly TARGETING_KEYS from insights routes/api/v1/validators.js - all
// 13 - so the console and /api/v1 offer an advertiser the same dimensions.
//
// The correspondence has to hold in both directions. A filter here with no key
// there renders a control whose value is silently stripped on save
// (sanitizeSegment rebuilds `segment` from that allowlist). A key there with no
// filter here is worse in a quieter way: an API client can set the dimension and
// the owner then cannot see or change it in the console. `browser` and `adsize`
// were in that second state until they were added below.
//
// Everything describing INVENTORY is absent by that rule - publishers, sites and
// placements (the spec's Layer 6), and equally div ids (a publisher's page
// markup) and declared/predicted IAB categories (what a publisher asserts about
// its own pages). An advertiser must never target or discover named inventory.
//
// `showExclude` opts every filter here into the Exclude toggle, which the shared
// module hides for any role other than admin. Exclusion is a normal self-serve
// need - a country to stay out of, a domain blocklist - and it leaks nothing:
// the dimensions an advertiser must not see are absent from this tab entirely,
// so an Exclude on what remains only narrows their own campaign. The insights
// service already accepts it (sanitizeSegment keeps whole filter objects for
// allowlisted keys), so this is a UI-visibility change only.
//
// `label` comes from the call site, not from a literal here (NBU-9): a tab
// written as a wrapper component keeps its label private, and ErrorAwareFormTabs
// can only badge a tab whose label it can read off the element. Declaring the
// labels where the tabs are listed also puts the whole tab strip in one place.
const FiltersTab = (props) => {
  const filterProps = { ...props, showExclude: true };
  return (
    <FormTab {...props}>
      <CountryFilter {...filterProps} />
      <StateFilter {...filterProps} />
      <DeviceTypeFilter {...filterProps} />
      <OSFilter {...filterProps} />
      {/* Browser has no dedicated component - the ops form builds it from the
          generic MultipleChoiceFilter too (adcampaigns.js), and these props must
          match it: the match type is what the adserver compares with, so a
          different one here would mean the two forms wrote incompatible
          documents for the same dimension. */}
      <MultipleChoiceFilter {...filterProps} title="Browsers" filterName="filter_browser"
        matchType="equalsIgnoreCase" matchTypeHidden choices={browserChoices} />
      <ChannelFilter {...filterProps} defaultValues={['web', 'amp']} />
      <ImpressionTypeFilter {...filterProps} defaultValues={['BANNER']} />
      <AdSizeFilter {...filterProps} />
      <TopLevelDomainFilter {...filterProps} />
      <PageUrlFilter {...filterProps} />
      <ViewabilityFilter {...filterProps} />
      <AsnFilter {...filterProps} />
      {/* Pointed at advertiser_ip_lists so the picker only ever offers the
          advertiser's own lists. The server enforces the same thing
          independently (advertiser_guard's checkIpListReferences), because the
          adserver resolves a list by bare code with no tenancy check. */}
      <IpListsFilter {...filterProps} reference="advertiser_ip_lists" />
      <UserAgentFilter {...filterProps} />
    </FormTab>
  );
};

// Direct FormTab children (mirroring FiltersTab below), not a wrapper
// nested inside a FormTab: TabbedForm/FormTab inject record/resource/basePath/
// variant/margin onto their IMMEDIATE child element. A wrapper wedged inside a
// literal <FormTab> would swallow those injected props unless it destructured
// and re-forwarded every one of them by name - a trap for the next field
// added here that actually needs one. Consuming only `isCreate` and
// forwarding the rest via {...rest} keeps that automatic either way.
// `label` is supplied by the call site - see the note on FiltersTab.
const SettingsFields = ({ isCreate, ...rest }) => (
  <FormTab {...rest}>
    {!isCreate && <TextField source="code" label="Campaign ID" />}
    {/* Explains whichever gate is actually stopping delivery - a budget limit,
        the Active switch, the flight window, or the account spend limit -
        rather than only the account case (NBU-7).

        Edit only. On Create there is no campaign to have a status yet, and an
        empty record resolves to "Off" (active defaults to false), so this would
        greet every new campaign with a red banner telling the advertiser their
        unsaved campaign is switched off. */}
    {!isCreate &&
      <FunctionField addLabel={false} render={record => <CampaignStatusBanner record={record} />} />}

    {/* PERFORMANCE FIRST, above the inputs.
        Answering "how is it doing" needs no scrolling, and when the answer is
        "badly" the inputs that fix it are directly underneath. It used to sit
        below the whole form, so the delivery story was the last thing found.

        SpendPacingChart reads GET /console/realtime_report, which is scoped to
        the caller's own advertisers server-side by realtime.applyAdvertiserScope
        (NBU-7) - before that, campaign_id was an unvalidated parameter and this
        chart would have been a way to read another advertiser's spend series.
        It is driven from formData because it needs `code`, and since a hidden
        FormTab never intersects, its own IntersectionObserver defers the fetch
        until the tab is actually shown.

        CampaignPerformancePanel owns the two-up layout, shared with the ops form
        so neither screen drifts. Edit-only: on Create every counter is zero and
        there is no campaign id to chart. */}
    {!isCreate &&
      <FormDataConsumer>
        {({ formData }) => (
          <CampaignPerformancePanel
            record={formData}
            campaignId={formData.code}
            hourlyLimit={formData.hourly_revenue_limit}
            dailyLimit={formData.daily_revenue_limit} />
        )}
      </FormDataConsumer>}
    <br />

    {/* Two side-by-side cards, mirroring the ops campaign form
        (adcampaigns.js): campaign identity on the left, everything about money
        on the right. Bid price moved in beside the spend limits because they
        are one decision - what this campaign costs - and were previously split
        across two tabs, so setting a budget meant leaving the screen the price
        was on.

        Note the inputs here are NOT direct FormTab children, so they receive
        none of the record/variant/margin props FormTab injects into its
        immediate children. That is fine, and is what the ops form has always
        done: react-admin inputs reach the form through context, not through
        injected props. It also keeps them out of FormInput's per-child
        wrapper, which is why each one needs an explicit width. */}
    <div style={{ width: '100%' }}>
      <div style={twoColumnLeft}>
        <Card raised>
          <CardHeader title="Campaign" />
          <CardContent>
            <BooleanInput source="active" label="Active" defaultValue={false} />
            <TextInput source="name" label="Campaign Name" style={cardInput} validate={validCampaignName} />
            <ReferenceArrayInput label="Creatives" perPage={15}
              sort={{ field: 'name', order: 'ASC' }}
              filterToQuery={searchText => ({ search_name: searchText })}
              source="creatives" reference="creatives" basePath="creatives" style={cardInput}>
              <AutocompleteArrayInput
                optionText={record => (record && record.name) ? `${record.name} (${record.code})` : ''}
                optionValue="code" validate={required()} />
            </ReferenceArrayInput>
          </CardContent>
        </Card>
      </div>
      <div style={twoColumnRight}>
        <Card raised>
          {/* The hourly-or-daily requirement is stated once on the card rather
              than repeated on both fields, then each field says which one
              satisfies it. Without this the rule was invisible: an advertiser
              could leave both blank, save successfully, and get a campaign that
              looks active and simply never bids. */}
          <CardHeader title="Budget"
            subheader="An hourly or a daily spend limit is required. With neither, no budget is ever released to the campaign and it will not serve." />
          <CardContent>
            <NumberInput source="bid_price" label="Bid Price (USD CPM)" style={cardInput}
              step={0.01} validate={[required(), number(), minValue(0.01)]}
              helperText="What you pay per 1000 impressions served." />
            {/* Directly under the bid, because it decides what a win actually
                costs against that bid. Advertiser-specific helper text: the ops
                copy names the bidder fallback, and per NBU-5 no supply-side
                identity reaches an advertiser role. */}
            <utils.AuctionTypeSelectInput style={cardInput}
              helperText={utils.AUCTION_TYPE_HELPER_TEXT_ADVERTISER} />
            {/* Sits between the price and the limits, as on the ops form: it
                governs how fast the money below is released, and is a different
                control from Campaign Pace on the Limits tab. Shared component,
                so both forms describe it identically. */}
            <utils.BudgetPacingSelectInput style={cardInput} />
            {/* requireSomeSpendLimit is the same cross-field validator the ops
                form uses (adcampaigns.js), pointed at the opposite field so
                filling either one clears the error on both. */}
            <NumberInput label="Hourly Spend Limit (USD)" source="hourly_revenue_limit"
              step={0.01} style={cardInput}
              validate={[requireSomeSpendLimit('daily_revenue_limit'), number(), minValue(0)]}
              helperText="The most this campaign may spend in one hour. Not needed if you set a daily limit." />
            <NumberInput label="Daily Spend Limit (USD)" source="daily_revenue_limit"
              step={0.01} style={cardInput}
              validate={[requireSomeSpendLimit('hourly_revenue_limit'), number(), minValue(0)]}
              helperText="The most this campaign may spend in one day. Not needed if you set an hourly limit." />
            <NumberInput label="Lifetime Spend Limit (USD)" source="lifetime_revenue_limit"
              step={0.01} validate={[number(), minValue(0)]} style={cardInput}
              helperText="Optional total for the whole campaign. Cannot exceed your monthly spend limit." />
            <NumberInput label="Lifetime Impressions Limit" source="lifetime_impressions_limit"
              step={1} validate={[number(), minValue(0)]} style={cardInput}
              helperText="Optional. Stops the campaign after this many impressions." />
          </CardContent>
        </Card>
      </div>
    </div>
    <br />
    <br />

    {/* Reads the saved RECORD, not form state, so a creative just added to the
        selector appears here only after Save - identical to the ops form, and
        the honest behaviour: an unsaved attachment is not attached yet. */}
    {!isCreate &&
      <ReferenceArrayField label="Attached creatives" reference="creatives"
        source="creatives" basePath="/creatives">
        <SingleFieldList linkType="edit">
          <ChipField source="name" clickable />
        </SingleFieldList>
      </ReferenceArrayField>}
  </FormTab>
);

// Pacing and scheduling: they shape WHEN the budget is spent, not how much of
// it there is, so they stay off the Settings tab. Named and laid out like the
// ops form's own "Limits" tab, minus day parting (never offered to advertisers).
// `label` is supplied by the call site - see the note on FiltersTab.
const LimitsFields = (props) => (
  <FormTab {...props}>
    <br />
    <SelectInput label="Campaign Pace" source="pace" initialValue="100" choices={utils.PACES} />
    <br />
    <Card style={{ width: '50%' }} raised>
      <CardHeader title="Frequency Capping" />
      <CardContent>
        <Box display="flex" style={{ gap: '16px' }}>
          <NumberInput label="Cap" source="frequency_cap.cap" min={0} step={1} validate={validFrequencyCap} />
          <NumberInput label="Hours" source="frequency_cap.hours" min={0} max={168} step={1} validate={validFrequencyHours} />
        </Box>
      </CardContent>
    </Card>
    <br />
    <Card style={{ width: '50%' }} raised>
      <CardHeader title="Flight Dates" />
      <CardContent>
        <Box display="flex" style={{ gap: '16px' }}>
          <DateInput source="flight_start_day" label="Flight start (day)" />
          <DateInput source="flight_end_day" label="Flight end (day)" />
        </Box>
        {/* The shared picker, so this can never drift from the ops form again. */}
        <utils.TimezoneSelectInput source="flight_timezone" label="Flight timezone"
          helperText="The timezone the flight start and end days are measured in." />
      </CardContent>
    </Card>
  </FormTab>
);

// The toolbar and the tabs are a pair (NBU-9): the toolbar names the fields
// that failed, the tabs mark and navigate to the one holding them. Validation
// on this form is spread across all three tabs - the spend limits and bid price
// on Settings, the frequency cap on Limits - and react-admin renders every tab
// and hides the inactive ones, so without this a blocked save reported only
// that "the form is not valid" with the offending field off screen.
export const AdvertiserCampaignEdit = (props) => (
  <Edit title="Campaign" undoable={false} {...props}>
    <TabbedForm toolbar={<utils.NoDeleteValidatingToolbar />} tabs={<ErrorAwareFormTabs />}>
      <SettingsFields label="Settings" isCreate={false} />
      <LimitsFields label="Limits" />
      <FiltersTab label="Filters" />
    </TabbedForm>
  </Edit>
);

export const AdvertiserCampaignCreate = (props) => (
  <Create title="New Campaign" {...props}>
    <TabbedForm toolbar={<utils.NoDeleteValidatingToolbar />} tabs={<ErrorAwareFormTabs />}>
      <SettingsFields label="Settings" isCreate />
      <LimitsFields label="Limits" />
      <FiltersTab label="Filters" />
    </TabbedForm>
  </Create>
);
